<template>
  <RootPageLayout title="Settings">
    <section class="settings-section">
      <div class="container">
        <template v-if="settingsView.isLoading">
          <loading-spinner />
        </template>
        <template v-if="settingsView.isError">
          <LoadingError />
        </template>
        <div v-if="settingsView.isLoaded" class="settings">
          <ul class="sidebar">
            <li class="sidebar__item">
              <div class="active-bar" />
              Account
              <span class="spacer" />
              <span class="iconfont iarrow active-arrow" />
            </li>
          </ul>
          <div class="content">
            <section class="settings__section settings__section--notifications">
              <h3 class="settings__section-title">Device Notifications</h3>
              <p class="settings__section-subtitle">
                Control notifications on devices where you use Shortform
              </p>

              <div
                v-if="
                  !settingsView.isDeviceNotificationsEnabled && !isNativeApp
                "
                class="notifications-disabled"
              >
                <h4 class="notifications-disabled__title">
                  Turn on browser notifications
                </h4>
                <p class="notifications-disabled__text">
                  Receive reminders that fit your schedule and help you keep
                  learning consistently
                </p>
              </div>

              <ul class="content__buttons">
                <li class="content__buttons__row">
                  <ToggleButton
                    class="settings__toggle"
                    :disabled="!settingsView.isDeviceNotificationsEnabled"
                    :value="
                      settingsView.notificationSettings?.notify_new_release ||
                      false
                    "
                    @update:value="updateNotifyNewRelease"
                    @click="requestNotificationsIfDisabled"
                  >
                    <p class="settings__toggle-title">
                      {{ notificationOptions.newRelease.title }}
                    </p>
                    <p
                      v-if="notificationOptions.newRelease.subtitle"
                      class="settings__toggle-subtitle"
                    >
                      {{ notificationOptions.newRelease.subtitle }}
                    </p>
                  </ToggleButton>
                </li>
                <li
                  v-if="returnToValue19051Variant() === 'B'"
                  class="content__buttons__row"
                >
                  <ToggleButton
                    class="settings__toggle"
                    :disabled="!settingsView.isDeviceNotificationsEnabled"
                    :value="
                      settingsView.notificationSettings
                        ?.notify_return_to_value || false
                    "
                    @update:value="updateNotifyReturnToValue"
                    @click="requestNotificationsIfDisabled"
                  >
                    <p class="settings__toggle-title">
                      {{ notificationOptions.returnToValue.title }}
                    </p>
                    <p class="settings__toggle-subtitle">
                      {{ notificationOptions.returnToValue.subtitle }}
                    </p>
                  </ToggleButton>
                </li>
              </ul>
            </section>

            <section class="settings__section settings__section--email">
              <h3 class="settings__section-title settings-email">
                Account Email
              </h3>
              <p class="settings__section-subtitle email-address__field">
                {{ email }}
              </p>
              <AppButton
                class="update-email__btn btns btns--yellow"
                @click="showChangeEmailPopup"
              >
                Change email
              </AppButton>
              <AppButton
                class="update-password__btn btns btns--yellow"
                @click="showChangePasswordPopup"
              >
                Change password
              </AppButton>
              <ChangePasswordPopup ref="popupChangePassword" />
              <ChangeEmailPopup ref="popupChangeEmail" @hide="recheckEmail" />
              <p
                v-if="popupChangePassword?.isSuccess()"
                class="update-password__label"
              >
                Password changed.
              </p>
              <p
                v-if="popupChangeEmail?.isSuccess()"
                class="update-email__label"
              >
                Email address changed.
              </p>
            </section>

            <section
              class="settings__section settings__section--delete-account"
            >
              <InfoDeleteAccount />
            </section>
          </div>
        </div>
      </div>
    </section>
  </RootPageLayout>
</template>

<script setup lang="ts">
import { onBeforeMount, onBeforeUnmount, ref } from 'vue'

import RootPageLayout from '@/app/components/RootPageLayout.vue'
import LoadingSpinner from '@/components/ui/LoadingSpinner.vue'
import AppButton from '@/app/components/button/AppButton.vue'
import ToggleButton from '@/components/ui/ToggleButton.vue'
import InfoDeleteAccount from '@/app/components/settings/InfoDeleteAccount.vue'
import ChangePasswordPopup from '@/app/components/settings/ChangePasswordPopup.vue'
import ChangeEmailPopup from '@/app/components/settings/ChangeEmailPopup.vue'

import { SettingsView } from '@/models/settings'
import { getEmail } from '@/services/auth'
import { returnToValue19051Variant } from '@/services/ab'
import { openNativeAppSettings } from '@/services/capacitor.appSettingsPlugin'
import { AppState } from '@capacitor/app'
import { emitter } from '@/services/mitt'
import { platform } from '@/services/platform'
import { notificationService } from '@/services/notification'

const settingsView = ref(new SettingsView())
const email = ref<string | null>(null)
const isNativeApp = ref(false)

const popupChangePassword = ref<InstanceType<
  typeof ChangePasswordPopup
> | null>(null)

const popupChangeEmail = ref<InstanceType<typeof ChangeEmailPopup> | null>(null)

const notificationOptions = {
  newRelease: {
    title: 'New announcements',
    subtitle:
      'Updates about new summaries and content selected especially for you',
  },
  returnToValue: {
    title: 'Daily practice',
    subtitle: "We'll send reminders at a time that works best for you",
  },
}

async function updateNotifyNewRelease(value: boolean): Promise<void> {
  await settingsView.value.toggleNotificationSetting(
    'notify_new_release',
    value,
  )
}

async function updateNotifyReturnToValue(value: boolean): Promise<void> {
  await settingsView.value.toggleNotificationSetting(
    'notify_return_to_value',
    value,
  )
}

/**
 * Request notifications permission if it hasn't been granted.
 */
async function requestNotificationsIfDisabled(
  event: PointerEvent,
): Promise<void> {
  if (settingsView.value.isDeviceNotificationsEnabled) {
    return
  }

  event.preventDefault()

  if (!isNativeApp.value) {
    return
  }

  const notificationsPermission = await notificationService.checkPermission()
  if (notificationsPermission === 'denied') {
    // If the user explicitly denied notifications, we can't
    // request them again, so we open the notification settings
    // page, so they can manually enable them.
    await openNativeAppSettings()
  } else {
    // Otherwise, we request notifications permission.
    await notificationService.init(true)
    await settingsView.value.updateNotificationPermissions()
  }
}

async function onAppResume(appState: AppState): Promise<void> {
  if (appState.isActive) {
    // Update the notification permission state in the view after
    // the user comes back to the app (presumably after going to
    // the device's settings page).
    await settingsView.value.updateNotificationPermissions()
  }
}

function showChangePasswordPopup(): void {
  if (!popupChangePassword.value) {
    return
  }

  popupChangePassword.value.show()
}

function showChangeEmailPopup(): void {
  if (!popupChangeEmail.value) {
    return
  }

  popupChangeEmail.value.show()
}

function recheckEmail(): void {
  // The popupChangeEmail dialog has closed.
  // Re-check the email address in case it was successfully changed.
  email.value = getEmail()
}

onBeforeMount(async () => {
  isNativeApp.value = await platform.isNativeApp()
  await settingsView.value.init()
  email.value = getEmail()
  emitter.on('appVisibilityChange', onAppResume)
})

onBeforeUnmount(() => {
  emitter.off('appVisibilityChange', onAppResume)
})
</script>

<style scoped lang="scss">
.settings-section {
  margin-top: 40px;
}

.settings-email {
  margin-top: 24px;
}

.settings {
  display: flex;
  flex-flow: row nowrap;
}

.sidebar {
  display: none;
}

.settings__toggle-title {
  margin: 0;
}

@media screen and (min-width: 900px) {
  .sidebar {
    display: block;
    padding: 0px;
    flex-grow: 1;
  }
}

.sidebar__item {
  max-width: 200px;

  display: flex;
  flex-flow: row-nowrap;

  line-height: 40px;
}

.active-bar {
  box-sizing: border-box;
  height: 40px;
  width: 4px;

  margin-right: 12px;

  background-color: #ffc906;
}

.spacer {
  flex-grow: 1;
}

.active-arrow {
  padding-top: 2px;
}

.content {
  flex-grow: 2;
}

.content__buttons {
  display: grid;
  gap: 24px;
  padding: 0px;
}

.update-password__btn,
.update-email__btn {
  margin-right: 12px;
  margin-bottom: 12px;
}

.update-password__label,
.update-email__label {
  margin: 12px 0;
}

.settings-section {
  margin-top: 0;
  padding-top: 28px;

  .content__buttons {
    gap: 0;
  }

  .content__buttons__row {
    padding-block: 14px;
    border-bottom: 1px solid $c-border-7;

    &:last-child {
      border: none;
      padding-bottom: 0;
    }
  }

  .settings {
    &__section {
      max-width: 552px;
      padding-bottom: 20px;

      @media (max-width: 899px) {
        margin-inline: auto;
      }

      &:not(:last-child) {
        border-bottom: 1px solid $c-border-7;
      }
    }

    &__section-title,
    :deep(.settings__section-title) {
      margin-bottom: 4px;

      @media (max-width: $w-mobile-s) {
        margin-bottom: 0;
        font-size: 20px;
        line-height: 23px;
      }
    }

    &__section-subtitle,
    :deep(.settings__section-subtitle) {
      font-size: 18px;
      line-height: 28px;
      margin-bottom: 20px;

      @media (max-width: $w-mobile-s) {
        font-size: 14px;
        line-height: 20px;
        margin-bottom: 12px;
      }
    }

    :deep(.toggle-button) {
      gap: 16px;
    }

    :deep(.toggle-button__label) {
      order: -1;
      flex: 1;
      min-width: 0;
    }

    &__toggle[disabled='true'] {
      cursor: auto;

      :deep(.toggle-button__slider) {
        background-color: $c-black-13;

        &:before {
          transform: none;
        }
      }
    }

    &__toggle-title {
      margin-bottom: 2px;
      font-size: 18px;
      line-height: 23px;
      color: $c-black-2;

      @media (max-width: $w-mobile-s) {
        font-size: 16px;
        line-height: 1.3;
      }
    }

    &__toggle-subtitle {
      font-size: 14px;
      line-height: 20px;
      color: $c-black-6;
    }
  }
}

.notifications-disabled {
  display: grid;
  padding: 16px 20px;
  border-radius: 16px;
  background: $c-bg;
  box-shadow: inset 0 0 8px rgba($c-black-2, 0.05);
  max-width: 600px;
  margin-bottom: 24px;

  &__title {
    font-size: 18px;
    line-height: 1.2;
    margin-bottom: 4px;

    @media (max-width: $w-mobile-s) {
      font-size: 16px;
    }
  }

  &__text {
    font-size: 16px;
    line-height: 1.3;
    margin-bottom: 0;

    @media (max-width: $w-mobile-s) {
      font-size: 14px;
    }
  }

  + .content__buttons {
    opacity: 0.4;
  }
}

.night-mode {
  .notifications-disabled {
    background: $c-dark-2;

    &__text {
      color: $c-black-6;
    }
  }

  .settings-section {
    .content__buttons__row {
      border-color: $c-black-8;
    }

    .settings {
      &__section {
        border-color: $c-black-8;
      }

      &__section-subtitle,
      :deep(.settings__section-subtitle) {
        color: $c-black-4;
      }

      &__toggle-title {
        color: $c-white;
      }

      &__toggle[disabled='true'] {
        :deep(.toggle-button__slider) {
          background-color: $c-dark-8;
        }
      }
    }
  }
}
</style>
